import {
  BooleanInput,
  Edit,
  FunctionField,
  Labeled,
  NumberField,
  NumberInput,
  required,
  TabbedForm,
  TextInput,
  useCanAccess,
} from 'react-admin';
import { Box, Divider, Grid2 as Grid } from '@mui/material';
import { ProductResource } from '../../Entities/Product';
import TzDateField from '../../Components/TzDate/TzDateField';
import { ReactElement } from 'react';
import JsonApiHasOneReferenceInput from '../../Components/JsonApiHasOneReferenceInput';
import { CurrencyAmountInput } from '../../Components/CurrencyAmount/CurrencyAmountInput';
import ImageUrlInput from '../../Components/ImageUrlInput';
import ProductLabelsTab from './LabelsTab';
import ProductToolbar from './Toolbar';

export default function ProductEdit(): ReactElement {
  const categoryAccess = useCanAccess({ resource: 'categories', action: 'list' });
  const manufacturerAccess = useCanAccess({ resource: 'manufacturers', action: 'list' });
  const canListCategories = categoryAccess.canAccess ?? false;
  const canListManufacturers = manufacturerAccess.canAccess ?? false;

  const prefetch: string[] = [];
  if (canListCategories) prefetch.push('category');
  if (canListManufacturers) prefetch.push('manufacturer');

  return (
    <Edit<ProductResource> mutationMode="pessimistic" queryOptions={{ meta: { prefetch } }}>
      <TabbedForm toolbar={<ProductToolbar mode="edit" />}>
        <TabbedForm.Tab label="Details">
          <Grid container spacing={2} sx={{ width: '100%' }}>
            <Grid size={{ xs: 12, xl: 3 }}>
              <Labeled>
                <FunctionField
                  label="Link"
                  render={(record?: Partial<ProductResource>) => {
                    const name = record?.attributes?.name;
                    const sluggifiedName = record?.attributes?.sluggified_name;

                    if (!name || !sluggifiedName) {
                      return <i>Unknown</i>;
                    }

                    return (
                      <a
                        href={`https://www.getprice.com.au/product/${sluggifiedName}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {sluggifiedName}
                      </a>
                    );
                  }}
                />
              </Labeled>
            </Grid>
            <Grid size={{ xs: 12, xl: 3 }}>
              <Box flex={1}>
                <Labeled>
                  <NumberField label="Shop Count" source="attributes.shop_count" />
                </Labeled>
              </Box>
            </Grid>
            <Grid size={{ xs: 12, xl: 3 }}>
              <Box flex={1}>
                <Labeled>
                  <NumberField label="Views" source="attributes.views_count" />
                </Labeled>
              </Box>
            </Grid>
            <Grid size={{ xs: 12, xl: 3 }}>
              <Box flex={1}>
                <Labeled>
                  <TzDateField label="Last Edited" source="attributes.edited" showTime />
                </Labeled>
              </Box>
            </Grid>
          </Grid>
          <Divider flexItem sx={{ mb: 2, mt: 2 }} />
          <Grid container spacing={2} sx={{ width: '100%' }}>
            <Grid size={{ xs: 12, xl: 4 }}>
              <Box flex={1}>
                <TextInput label="Name" source="attributes.name" fullWidth />
              </Box>
              <Box flex={1}>
                <TextInput label="Custom Title" source="attributes.custom_title" fullWidth />
              </Box>
              <Box flex={1}>
                <TextInput label="Model" source="attributes.model" fullWidth />
              </Box>
              <Box flex={1}>
                <JsonApiHasOneReferenceInput<ProductResource, 'manufacturer', 'manufacturers'>
                  label="Manufacturer"
                  optionText="attributes.name"
                  source="relationships.manufacturer"
                  reference="manufacturers"
                  isRequired
                  validate={[required()]}
                />
              </Box>
              <Box flex={1}>
                <TextInput
                  label="Description"
                  source="attributes.description"
                  multiline
                  fullWidth
                />
              </Box>
              <Box flex={1}>
                <TextInput label="Keywords" source="attributes.keywords" multiline fullWidth />
              </Box>
              <Box flex={1}>
                <JsonApiHasOneReferenceInput<ProductResource, 'category', 'categories'>
                  label="Category"
                  optionText="attributes.full_name"
                  source="relationships.category"
                  reference="categories"
                />
              </Box>
            </Grid>
            <Grid size={{ xs: 12, xl: 2 }}>
              <CurrencyAmountInput
                label="Min Price (AUD)"
                source="attributes.min_price"
                step={0.01}
                fullWidth
              />
              <CurrencyAmountInput
                label="Max Price (AUD)"
                source="attributes.max_price"
                step={0.01}
                fullWidth
              />
              <NumberInput
                label="Future Publishing ID"
                source="attributes.future_publishing_id"
                fullWidth
              />
              <TextInput label="MPN" source="attributes.mpn" fullWidth />
              <TextInput label="UPC" source="attributes.upc" fullWidth />
            </Grid>
            <Grid size={{ xs: 12, xl: 6 }}>
              <BooleanInput label="Active?" source="attributes.status" />
              <BooleanInput label="Is Prioritized?" source="attributes.is_prioritized" />
              <ImageUrlInput source="attributes.image_url" label="Image" />
            </Grid>
          </Grid>
        </TabbedForm.Tab>
        {canListCategories && <ProductLabelsTab />}
      </TabbedForm>
    </Edit>
  );
}
